import React, { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { reduxForm } from 'redux-form'
import { Col, Row, Card, Button } from "react-bootstrap"
import { 
    Box, 
    Typography, 
    TextField, 
    Dialog as MuiDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button as MuiButton
} from '@material-ui/core'
import Overview from 'components/Applicants/partial/Tabs/Summary/Overview'
import Summary from 'components/Applicants/partial/Tabs/Summary/Summary'
import SummaryCard from 'components/Applicants/ApplicationDetail/SummaryCard'
import { Dialog } from 'components/shared/Dialog'
import { Status } from 'constants/chartData'
import { purchaseApplicationOffer, declineApplicationOffer } from 'actions/applicationOffers'
import { Toast } from 'components/shared/Toaster'
import './ApplicationOfferDetail.scss'

const ApplicationOfferHeader = ({ name, status, applicantUUID, onPurchase, onDecline, isProcessing }) => {
    const statusInfo = Status.find(st => st.status === status)
    
    return (
        <div className='applicant-header-row'>
            <div className='applicant-header-left'>
                <div className='applicant-details'>
                    <div className='applicant-name-and-status'>
                        <h4 className='mb-0 mr-3'>{name || 'Application Offer'}</h4>
                        {statusInfo && (
                            <span 
                                className='badge badge-pill status-badge' 
                                style={{ 
                                    backgroundColor: statusInfo.code, 
                                    color: statusInfo.color,
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    padding: '0.5rem 1rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                {statusInfo.abbreviation}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className='applicant-header-right'>
                <div className='action-buttons'>
                    <Button 
                        variant="success" 
                        onClick={onPurchase}
                        disabled={isProcessing}
                        className="mr-2"
                    >
                        {isProcessing ? 'Processing...' : 'Purchase'}
                    </Button>
                    <Button 
                        variant="outline-danger" 
                        onClick={onDecline}
                        disabled={isProcessing}
                    >
                        Decline
                    </Button>
                </div>
            </div>
        </div>
    )
}

const ApplicationOfferDetail = (props) => {
    const {
        roxMatchId, // application offer id from params
        applicationOfferDetail,
        availableSummary,
        // Marketplace actions for application offers
        getApplicationOfferById,
        getApplicationOfferAvailableSummary,
        getApplicationOfferSummary,
        // Additional marketplace functions for components
        getApplicationOfferSummaryInfo,
        requestApplicationOfferInfo,
        getApplicationOfferSummaryReportByEntity,
        getApplicationOfferAvailableSummaryForComponents,
        getApplicationOfferBusinessReportSummary,
        getApplicationOfferOwnerReportSummary
    } = props

    const [loading, setLoading] = useState(true)
    const [loadedApp, setLoadedApp] = useState(false)
    const [updateSummary, setUpdateSummary] = useState(0)
    const [isProcessing, setIsProcessing] = useState(false)
    const [showPurchaseDialog, setShowPurchaseDialog] = useState(false)
    const [showDeclineDialog, setShowDeclineDialog] = useState(false)
    const [declineReason, setDeclineReason] = useState('')
    
    // Get client info and user role
    const dispatch = useDispatch()
    const history = useHistory()
    const client = useSelector(state => state.user?.userDetails?.securityContext?.client || {})

    // Application data from the marketplace state
    const business = applicationOfferDetail?.business || applicationOfferDetail
    const status = applicationOfferDetail?.status || applicationOfferDetail?.applicationStatus
    const owners = applicationOfferDetail?.owners || []
    const debtor = applicationOfferDetail?.debtor

    
    const getApplicationOfferData = useCallback(async () => {
        if (!roxMatchId) return
        
        setLoading(true)
        try {
            await getApplicationOfferById(roxMatchId)
            
            try {
                await Promise.all([
                    getApplicationOfferAvailableSummary && getApplicationOfferAvailableSummary(roxMatchId),
                    getApplicationOfferSummary && getApplicationOfferSummary(roxMatchId, 'UCC')
                ])
            } catch (summaryError) {
                console.warn('Error fetching summary data:', summaryError)
            }
            
        } catch (error) {
            console.error('Error fetching application offer data:', error)
        } finally {
            setLoading(false)
        }
    }, [roxMatchId, getApplicationOfferById, getApplicationOfferAvailableSummary, getApplicationOfferSummary])

    const getApplicationOffer = useCallback(() => {
        setLoadedApp(false)
        getApplicationOfferData().finally(() => {
            setLoadedApp(true)
        })
    }, [getApplicationOfferData])

    useEffect(() => {
        if (!loadedApp) {
            getApplicationOffer()
        }
    }, [getApplicationOffer, loadedApp])
    
    useEffect(() => {
        if (applicationOfferDetail && !loadedApp) {
            setLoadedApp(true)
            setLoading(false)
        }
    }, [applicationOfferDetail, loadedApp])
    
    const handleUpdateSummaryCard = () => {
        setUpdateSummary(updateSummary + 1)
    }

    const handlePurchase = () => {
        setShowPurchaseDialog(true)
    }

    const handleDecline = () => {
        setDeclineReason('')
        setShowDeclineDialog(true)
    }

    const confirmPurchase = async () => {
        if (!roxMatchId || isProcessing) return
        
        setIsProcessing(true)
        try {
            await purchaseApplicationOffer(roxMatchId)()
            Toast({ type: 'success', message: 'Application offer purchased successfully!' })
            history.push('/application-offers')
        } catch (error) {
            Toast({ type: 'error', message: 'Failed to purchase application offer. Please try again.' })
        } finally {
            setIsProcessing(false)
            setShowPurchaseDialog(false)
        }
    }

    const confirmDecline = async () => {
        if (!roxMatchId || isProcessing) return
        
        if (!declineReason.trim()) {
            Toast({ type: 'error', message: 'Please provide a reason for declining.' })
            return
        }
        
        setIsProcessing(true)
        try {
            await declineApplicationOffer(roxMatchId, declineReason.trim())()
            Toast({ type: 'success', message: 'Application offer declined successfully!' })
            history.push('/application-offers')
        } catch (error) {
            Toast({ type: 'error', message: 'Failed to decline application offer. Please try again.' })
        } finally {
            setIsProcessing(false)
            setShowDeclineDialog(false)
            setDeclineReason('')
        }
    }

    if (loading) {
        return (
            <div className="application-offer-detail">
                <div className="loading-container">
                    <div>Loading application offer details...</div>
                </div>
            </div>
        )
    }

    if (!applicationOfferDetail) {
        return (
            <div className="application-offer-detail">
                <div className="error-container">
                    <div>Application offer not found</div>
                </div>
            </div>
        )
    }

    return (
        <div className='applicant'>
            {loadedApp &&
                <div>
                    <ApplicationOfferHeader
                        name={business?.name || business?.applicantName}
                        status={status}
                        applicantUUID={roxMatchId}
                        onPurchase={handlePurchase}
                        onDecline={handleDecline}
                        isProcessing={isProcessing}
                    />
                    <Row>
                        {!client?.hideDecisioning &&
                            <Col md={3} className='pl-3 pr-0'>
                                <SummaryCard 
                                    applicantId={roxMatchId} 
                                    updates={updateSummary}
                                    isReadOnly={true}
                                    getSummaryInfoFn={getApplicationOfferSummaryInfo}
                                    isApplicationOffer={true}
                                />
                            </Col>
                        }
                        <Col md={!client?.hideDecisioning ? 9 : 12} className='pl-3 pr-3'>
                            <div className='appDetailTab mt-3'>
                                <Card className='mb-4'>
                                    <Card.Header>
                                        <h5 className='mb-0'>Overview</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Overview
                                            applicantId={roxMatchId}
                                            businessData={business}
                                            debtorData={debtor}
                                            ownersData={owners}
                                            formMode={false} // Read-only mode
                                            modalMode={false} // No modals in read-only
                                            hideFmcsa={false}
                                            isBrokerCustomer={true}
                                            getSalesRepresentatives={() => {}} // Empty function for read-only
                                            // Redux Form props (required by Overview component)
                                            handleSubmit={() => {}} // Empty function for read-only
                                            reset={() => {}} // Empty function for read-only
                                            dirty={false} // No dirty state in read-only
                                            change={() => {}} // Empty function for read-only
                                            dispatch={dispatch} // Pass dispatch
                                            isReadOnly={true}
                                            hideUpdateButtons={true}
                                            hideGetDataButtons={true}
                                            requestApplicationInfoFn={requestApplicationOfferInfo}
                                        />
                                    </Card.Body>
                                </Card>
                                <Card>
                                    <Card.Header>
                                        <h5 className='mb-0'>Summary</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Summary
                                            sosSearchEnabled={client.sosSearchEnabled}
                                            applicantId={roxMatchId}
                                            anonymize={true}
                                            updates={updateSummary}
                                            changeTab={() => {}} // No tab changes needed
                                            isGetAllData={false}
                                            handleGetAllData={() => {}} // Disabled for Application Offers
                                            updateSummaryCard={handleUpdateSummaryCard}
                                            setDisplayGAD={() => {}} // Disabled
                                            dispatchProcessing={() => {}} // Disabled
                                            debtorApplication={false}
                                            isBrokerCustomer={true}
                                            isReadOnly={true}
                                            hideUpdateButtons={true}
                                            hideGetDataButtons={true}
                                            getAvailableSummaryFn={getApplicationOfferAvailableSummaryForComponents}
                                            getSummaryReportByEntityFn={getApplicationOfferSummaryReportByEntity}
                                            availableSummaryData={availableSummary}
                                            getBusinessReportSummaryFn={getApplicationOfferBusinessReportSummary}
                                            getOwnerReportSummaryFn={getApplicationOfferOwnerReportSummary}
                                        />
                                    </Card.Body>
                                </Card>
                            </div>
                        </Col>
                    </Row>
                </div>
            }
            
            {/* Purchase Confirmation Dialog */}
            <Dialog
                fullWidth
                maxWidth='sm'
                type={'confirm'}
                open={showPurchaseDialog}
                contentMessage={'Are you sure you want to purchase this application offer? This action cannot be undone.'}
                confirmButton={{ text: 'Purchase', disabled: isProcessing }}
                alertTiltle={'Confirm Purchase'}
                onSubmit={confirmPurchase}
                onClose={() => setShowPurchaseDialog(false)}
            />
            
            {/* Decline Confirmation Dialog */}
            <MuiDialog
                open={showDeclineDialog}
                onClose={() => {
                    setShowDeclineDialog(false)
                    setDeclineReason('')
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Decline Application Offer
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Are you sure you want to decline this application offer? This action cannot be undone.
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Reason for Declining *
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            value={declineReason}
                            onChange={(e) => setDeclineReason(e.target.value)}
                            placeholder="Please provide a reason for declining this application offer..."
                            disabled={isProcessing}
                            required
                            error={!declineReason.trim()}
                            helperText={declineReason.trim() ? `${declineReason.length} characters` : 'Please provide a reason for declining'}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <MuiButton
                        onClick={() => {
                            setShowDeclineDialog(false)
                            setDeclineReason('')
                        }}
                        disabled={isProcessing}
                    >
                        Cancel
                    </MuiButton>
                    <MuiButton
                        onClick={confirmDecline}
                        color="primary"
                        variant="contained"
                        disabled={isProcessing || !declineReason.trim()}
                        style={{ backgroundColor: '#dc3545', color: 'white' }}
                    >
                        {isProcessing ? 'Declining...' : 'Decline Application'}
                    </MuiButton>
                </DialogActions>
            </MuiDialog>
        </div>
    )
}

export default ApplicationOfferDetail